import React, { useEffect, useState, useRef } from "react";
import { Plus, Edit3, Trash2, UserCog, X, Mail, Building2, Check, KeyRound, ShieldCheck, ArrowRightLeft, Activity, Clock } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import SupervisorLoginModal from "@/components/SupervisorLoginModal";
import BranchManagers from "@/components/BranchManagers";

export default function Admins() {
  const { t } = useT();
  const { user } = useAuth();
  const scrollRef = useRef(null);
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", area_manager_id: "" });
  const [origAreaManagerId, setOrigAreaManagerId] = useState("");
  const [branchIds, setBranchIds] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [credsFor, setCredsFor] = useState(null); // admin row whose login modal is open
  const [moving, setMoving] = useState(null); // admin row to move
  const [moveTarget, setMoveTarget] = useState("");
  const [moveError, setMoveError] = useState("");
  const [areaManagers, setAreaManagers] = useState([]);
  const [activity, setActivity] = useState({}); // admin_id -> activity (EXPERIMENTAL)
  const [activityFor, setActivityFor] = useState(null); // activity modal
  const canManageLogins = user?.role === "area_manager" || user?.role === "super_admin";
  const canMove = canManageLogins; // area_manager or super_admin
  const [tab, setTab] = useState("sups"); // sups | bms
  const [otpBusy, setOtpBusy] = useState(false);

  const resetOtp = async (a) => {
    if (!window.confirm(t("ss_otp_confirm", { name: a.name }))) return;
    setOtpBusy(true);
    try {
      const r = await api.post(`/admins/${a.id}/reset-otp`);
      const chs = (r.data.channels || []).map((c) => t(`ss_ch_${c}`)).join("، ");
      setFeedback({ kind: "ok", text: t("ss_otp_sent", { channels: chs }) });
      if (r.data.user_telegram_contact) {
        window.prompt(t("ss_otp_tg_contact"), r.data.user_telegram_contact);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setFeedback({ kind: "error", text: typeof d === "string" ? d : t("save_failed") });
    }
    setOtpBusy(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [ad, br] = await Promise.all([api.get("/admins"), api.get("/branches")]);
      setItems(ad.data);
      setBranches(br.data);
      if (user?.role === "super_admin") {
        const am = await api.get("/area-managers");
        setAreaManagers(am.data || []);
      } else if (user?.role === "area_manager") {
        // Area managers can move to any OTHER active area manager
        const am = await api.get("/area-managers/peers").catch(() => ({ data: [] }));
        setAreaManagers(am.data || []);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!canManageLogins) return;
    api.get("/supervisors/activity").then((r) => {
      const map = {};
      (r.data || []).forEach((s) => { if (s.admin_id) map[s.admin_id] = s; });
      setActivity(map);
    }).catch(() => {});
  }, [canManageLogins]);

  const fmtSeen = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 5) return t("activity_online_now");
    if (mins < 60) return t("activity_mins_ago", { n: mins });
    if (mins < 60 * 24) return t("activity_hours_ago", { n: Math.floor(mins / 60) });
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) + " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  };
  const fmtDur = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

  const downloadActivity = async () => {
    try {
      const res = await api.get("/supervisors/activity/xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "supervisors_activity.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* noop */ }
  };

  // Reset scroll on modal open
  useEffect(() => {
    if (editing && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [editing]);

  const branchesForAdmin = (adminId) => branches.filter((b) => b.admin_id === adminId);

  const openNew = () => {
    setEditing({});
    setForm({ name: "", phone: "", email: "", area_manager_id: "" });
    setBranchIds([]);
    setOrigAreaManagerId("");
    setFeedback(null);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name, phone: a.phone || "", email: a.email || "", area_manager_id: a.area_manager_id || "" });
    setBranchIds(branchesForAdmin(a.id).map((b) => b.id));
    setOrigAreaManagerId(a.area_manager_id || "");
    setFeedback(null);
  };
  const close = () => { setEditing(null); setFeedback(null); };

  const save = async () => {
    if (!form.name.trim()) return;
    setFeedback(null);
    try {
      let adminId;
      // Payload for PUT/POST — exclude area_manager_id (handled via /move)
      const payload = { name: form.name, phone: form.phone, email: form.email };
      if (editing.id) {
        await api.put(`/admins/${editing.id}`, payload);
        adminId = editing.id;
      } else {
        const res = await api.post("/admins", payload);
        adminId = res.data.id;
      }
      // If Super Admin changed the Area Manager on an existing admin → move (cascades branches + employees)
      if (
        editing.id &&
        user?.role === "super_admin" &&
        form.area_manager_id &&
        form.area_manager_id !== origAreaManagerId
      ) {
        await api.post(`/admins/${adminId}/move`, { target_area_manager_id: form.area_manager_id });
      }
      // Always sync branches assignment
      await api.put(`/admins/${adminId}/branches`, { branch_ids: branchIds });
      close();
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      let msg;
      if (e.response?.status === 409 && d && typeof d === "object") {
        if (d.message === "duplicate_admin_name") {
          msg = t("duplicate_admin_name_error").replace("{name}", d.name || "");
        } else if (d.message === "duplicate_admin_email") {
          msg = t("duplicate_admin_email_error").replace("{name}", d.name || "").replace("{area_manager_name}", d.area_manager_name || "—");
        } else if (d.message === "duplicate_admin_phone") {
          msg = t("duplicate_admin_phone_error").replace("{name}", d.name || "").replace("{area_manager_name}", d.area_manager_name || "—");
        }
      }
      setFeedback({ kind: "error", text: msg || (typeof d === "string" ? d : e.message) });
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("delete_admin_confirm"))) return;
    await api.delete(`/admins/${id}`);
    load();
  };

  const openMove = (a) => {
    setMoving(a);
    setMoveTarget("");
    setMoveError("");
  };
  const closeMove = () => { setMoving(null); setMoveTarget(""); setMoveError(""); };
  const doMove = async () => {
    if (!moveTarget) { setMoveError(t("move_target_required")); return; }
    try {
      const res = await api.post(`/admins/${moving.id}/move`, { target_area_manager_id: moveTarget });
      closeMove();
      load();
      const name = moving.name;
      const targetName = areaManagers.find((am) => am.id === moveTarget)?.full_name || "";
      window.alert(
        res.data.already
          ? t("move_admin_already").replace("{name}", name)
          : t("move_admin_done")
              .replace("{name}", name)
              .replace("{target}", targetName)
              .replace("{branches}", res.data.moved_branches ?? 0)
              .replace("{employees}", res.data.moved_employees ?? 0)
      );
    } catch (e) {
      setMoveError(e.response?.data?.detail || e.message);
    }
  };

  const toggleBranch = (id) => {
    setBranchIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <div className="space-y-4" data-testid="admins-page">
      {canManageLogins && (
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
          <button onClick={() => setTab("sups")}
            className={`rounded-lg px-4 py-2 text-sm ${tab === "sups" ? "bg-white shadow-sm font-bold text-slate-800" : "font-semibold text-slate-500"}`}
            data-testid="admins-tab-sups">{t("nav_admins")}</button>
          <button onClick={() => setTab("bms")}
            className={`rounded-lg px-4 py-2 text-sm ${tab === "bms" ? "bg-white shadow-sm font-bold text-slate-800" : "font-semibold text-slate-500"}`}
            data-testid="admins-tab-bms">{t("bm_tab_title")}</button>
        </div>
      )}
      {tab === "bms" && canManageLogins ? <BranchManagers /> : (
      <>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-teal-900">{t("nav_admins")}</h2>
          <p className="text-gray-500 text-sm">{t("admins_subtitle")}</p>
        </div>
        <button className="btn btn-primary" onClick={openNew} data-testid="add-admin-button">
          <Plus size={16} /> {t("add_admin")}
        </button>
      </div>

      {/* EXPERIMENTAL: honor board — top supervisors by combined engagement (login + interaction) */}
      {(() => {
        const allRanked = Object.values(activity)
          .sort((a, b) => ((b.score || 0) - (a.score || 0)) || ((b.month_pct || 0) - (a.month_pct || 0)));
        if (!allRanked.some((s) => (s.score || 0) > 0)) return null;
        const ranked = allRanked.slice(0, 3);
        const rest = allRanked.slice(3);
        const tiers = [
          { medal: "🥇", label: t("honor_tier_gold"), ring: "bg-amber-100/80 border-amber-400", badge: "bg-amber-400 text-amber-950" },
          { medal: "🥈", label: t("honor_tier_silver"), ring: "bg-slate-100 border-slate-300", badge: "bg-slate-300 text-slate-800" },
          { medal: "🥉", label: t("honor_tier_bronze"), ring: "bg-orange-50 border-orange-300", badge: "bg-orange-300 text-orange-950" },
        ];
        const nameOf = (s) => items.find((a) => a.id === s.admin_id)?.name || s.full_name || s.username;
        return (
          <div className="bg-gradient-to-l from-amber-50 to-white border border-amber-200 rounded-2xl p-4" data-testid="honor-board">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🏆</span>
              <h3 className="font-black text-amber-800 text-sm">{t("honor_board_title")}</h3>
              <span className="text-[9px] font-black bg-violet-100 text-violet-700 rounded-full px-1.5 py-0.5">{t("trial_badge")}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {ranked.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => setActivityFor({ ...s, admin_name: nameOf(s) })}
                  className={`relative flex items-center gap-3 rounded-xl px-3 py-3 border text-start transition hover:shadow-md ${tiers[i].ring}`}
                  data-testid={`honor-rank-${i + 1}`}
                >
                  <span className={`absolute -top-2 start-2 text-[10px] font-black rounded-full px-2 py-0.5 shadow-sm ${tiers[i].badge}`}>
                    {i + 1} · {tiers[i].label}
                  </span>
                  <span className="text-3xl">{tiers[i].medal}</span>
                  <div className="min-w-0">
                    <div className="text-xs font-black text-slate-800 truncate">{nameOf(s)}</div>
                    <div className="text-[13px] font-black text-amber-700" data-testid={`honor-score-${i + 1}`}>
                      {t("honor_score", { n: s.score || 0 })}
                    </div>
                    <div className="text-[10px] font-bold text-slate-500">
                      {t("honor_breakdown", { login: s.month_pct || 0, interact: s.interaction_pct || 0 })}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {rest.length > 0 && (
              <div className="mt-2 space-y-1">
                {rest.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => setActivityFor({ ...s, admin_name: nameOf(s) })}
                    className="w-full flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg px-3 py-1.5 hover:bg-slate-100 transition"
                    data-testid={`honor-rest-${i + 4}`}
                  >
                    <span className="text-xs font-bold text-slate-600">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-black me-2">{i + 4}</span>
                      {nameOf(s)}
                    </span>
                    <span className="text-[11px] font-black text-slate-500">{t("honor_score", { n: s.score || 0 })}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      <div className="bg-white rounded-2xl card-shadow overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">{t("loading")}</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-gray-400">
            <UserCog size={36} className="mx-auto mb-3 opacity-50" />
            {t("no_admins")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t("name")}</th>
                  <th>{t("phone_col")}</th>
                  <th>{t("email")}</th>
                  <th>{t("assigned_branches")}</th>
                  <th>{t("activity_last_seen")}</th>
                  <th className="text-left">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => {
                  const myBranches = branchesForAdmin(a.id);
                  return (
                    <tr key={a.id} data-testid={`admin-row-${a.id}`}>
                      <td className="font-semibold">{a.name}</td>
                      <td className="text-gray-500">{a.phone || "—"}</td>
                      <td className="text-gray-500" style={{direction: "ltr", textAlign: "start"}}>
                        {a.email ? (
                          <span className="inline-flex items-center gap-1">
                            <Mail size={12} className="text-teal-600" /> {a.email}
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td>
                        {myBranches.length === 0 ? (
                          <span className="text-gray-300 text-sm">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 max-w-md">
                            {myBranches.slice(0, 4).map((b) => (
                              <span key={b.id} className="pill bg-teal-50 text-teal-700 border border-teal-200 text-[11px]">
                                {b.code ? `${b.code} · ` : ""}{b.name}
                              </span>
                            ))}
                            {myBranches.length > 4 && (
                              <span className="pill bg-gray-100 text-gray-600 text-[11px]">+{myBranches.length - 4}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {activity[a.id] ? (
                          <button
                            onClick={() => setActivityFor({ ...activity[a.id], admin_name: a.name })}
                            className="inline-flex items-center gap-1.5 text-xs font-bold rounded-lg px-2 py-1 bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 transition"
                            data-testid={`admin-activity-${a.id}`}
                            title={t("activity_details")}
                          >
                            <Activity size={12} />
                            {fmtSeen(activity[a.id].last_seen_at) || "—"}
                          </button>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="text-left">
                        {canManageLogins && (
                          <button
                            className={`btn btn-ghost ${a.has_login ? "text-emerald-700" : "text-teal-700"}`}
                            onClick={() => setCredsFor(a)}
                            data-testid={`login-admin-${a.id}`}
                            title={a.has_login ? t("supervisor_login_edit") : t("supervisor_login_create")}
                          >
                            <KeyRound size={14} /> {a.has_login ? (
                              <span className="hidden md:inline">{a.login_username}</span>
                            ) : (
                              <span className="hidden md:inline">{t("create_login")}</span>
                            )}
                          </button>
                        )}
                        <button className="btn btn-ghost" onClick={() => openEdit(a)} data-testid={`edit-admin-${a.id}`}>
                          <Edit3 size={14} /> <span className="hidden md:inline">{t("edit")}</span>
                        </button>
                        {canMove && (
                          <button
                            className="btn btn-ghost text-indigo-700 hover:bg-indigo-50"
                            onClick={() => openMove(a)}
                            data-testid={`move-admin-${a.id}`}
                            title={t("move_to_area")}
                          >
                            <ArrowRightLeft size={14} /> <span className="hidden lg:inline">{t("move")}</span>
                          </button>
                        )}
                        <button className="btn btn-danger mr-2" onClick={() => remove(a.id)} data-testid={`delete-admin-${a.id}`}>
                          <Trash2 size={14} /> <span className="hidden md:inline">{t("delete")}</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop" onClick={close}>
          <div ref={scrollRef} className="bg-white rounded-2xl card-shadow w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-lg font-bold text-teal-900">{editing.id ? t("edit_admin") : t("add_new_admin")}</h3>
              <button className="btn btn-ghost" onClick={close}><X size={16} /></button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="inp-label">{t("admin_name")}</label>
                  <input
                    className="inp"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t("admin_name_placeholder")}
                    autoFocus
                    data-testid="admin-name-input"
                  />
                </div>
                <div>
                  <label className="inp-label">{t("phone")}</label>
                  <input
                    className="inp"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder={t("phone_placeholder")}
                    data-testid="admin-phone-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="inp-label flex items-center gap-1">
                    <Mail size={12} /> {t("email")} <span className="text-xs text-gray-400 font-normal">({t("email_help")})</span>
                  </label>
                  <input
                    type="email"
                    className="inp"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="supervisor@example.com"
                    style={{direction: "ltr"}}
                    data-testid="admin-email-input"
                  />
                </div>
                {user?.role === "super_admin" && editing.id && (
                  <div className="md:col-span-2">
                    <label className="inp-label flex items-center gap-1">
                      <ShieldCheck size={12} /> {t("area_manager")}
                      <span className="text-xs text-gray-400 font-normal">({t("edit_admin_area_manager_help")})</span>
                    </label>
                    <select
                      className="inp"
                      value={form.area_manager_id}
                      onChange={(e) => setForm({ ...form, area_manager_id: e.target.value })}
                      data-testid="admin-area-manager-select"
                    >
                      <option value="">— {t("select_area_manager")} —</option>
                      {areaManagers.filter((am) => am.is_active !== false).map((am) => (
                        <option key={am.id} value={am.id}>
                          {am.full_name || am.username} ({am.username})
                        </option>
                      ))}
                    </select>
                    {form.area_manager_id && form.area_manager_id !== origAreaManagerId && (
                      <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2 text-amber-900 leading-relaxed" data-testid="area-manager-change-warning">
                        ⚠ {t("edit_admin_area_manager_warning")}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Branches assignment */}
              <div>
                <h4 className="text-sm font-bold text-teal-800 mb-2 border-r-4 border-teal-500 pr-2 flex items-center gap-2">
                  <Building2 size={14} /> {t("assigned_branches")}
                  {branchIds.length > 0 && (
                    <span className="pill status-valid text-[11px]">{t("branches_assigned_count", { n: branchIds.length })}</span>
                  )}
                </h4>
                <p className="text-xs text-gray-500 mb-3">{t("select_branches_for_admin")}</p>
                {branches.length === 0 ? (
                  <p className="text-sm text-gray-400 bg-gray-50 rounded-lg p-3 text-center">{t("no_branches_yet")}</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                    {branches.filter((b) => !b.is_vacation).map((b) => {
                      const checked = branchIds.includes(b.id);
                      const ownerOther = b.admin_id && b.admin_id !== editing.id && !checked;
                      return (
                        <label
                          key={b.id}
                          className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg cursor-pointer border transition ${
                            checked
                              ? "bg-teal-50 border-teal-300"
                              : ownerOther
                              ? "bg-amber-50 border-amber-200"
                              : "bg-white border-gray-200 hover:border-teal-300"
                          }`}
                          data-testid={`admin-branch-checkbox-${b.id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-teal-600 flex-shrink-0"
                              checked={checked}
                              onChange={() => toggleBranch(b.id)}
                            />
                            <span className="text-sm font-medium truncate">
                              {b.code && <span className="text-xs text-gray-400">[{b.code}] </span>}
                              {b.name}
                            </span>
                          </div>
                          {ownerOther && (
                            <span className="text-[10px] text-amber-700 whitespace-nowrap">⚠ مع آخر</span>
                          )}
                          {checked && <Check size={14} className="text-teal-600 flex-shrink-0" />}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {feedback && (
                <div className={`text-sm rounded-lg p-2 ${feedback.kind === "error" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
                  {feedback.text}
                </div>
              )}

              {canManageLogins && editing.id && (
                <div className="border-t border-gray-100 pt-4" data-testid="admin-account-mgmt">
                  <h4 className="text-sm font-bold text-teal-800 mb-2 border-r-4 border-teal-500 pr-2 flex items-center gap-2">
                    <KeyRound size={14} /> {t("acct_mgmt_title")}
                  </h4>
                  {editing.has_login ? (
                    <button
                      onClick={() => resetOtp(editing)}
                      disabled={otpBusy}
                      className="btn btn-secondary text-teal-700 border-teal-200 bg-teal-50 hover:bg-teal-100 disabled:opacity-50"
                      data-testid="admin-reset-otp-btn"
                    >
                      <KeyRound size={14} /> {otpBusy ? t("saving") : t("ss_reset_otp")}
                    </button>
                  ) : (
                    <p className="text-xs text-gray-400 bg-gray-50 rounded-lg p-2">{t("acct_no_login")}</p>
                  )}
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex justify-end gap-2 rounded-b-2xl">
              <button className="btn btn-secondary" onClick={close}>{t("cancel")}</button>
              <button className="btn btn-primary" onClick={save} data-testid="save-admin-button">{t("save")}</button>
            </div>
          </div>
        </div>
      )}

      {credsFor && (
        <SupervisorLoginModal
          admin={credsFor}
          onClose={() => setCredsFor(null)}
          onSaved={load}
        />
      )}

      {moving && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="move-admin-modal">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ArrowRightLeft size={18} className="text-indigo-600" />
                <h3 className="font-bold text-lg">{t("move_supervisor_title")}</h3>
              </div>
              <button className="btn btn-ghost" onClick={closeMove}><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <p className="text-gray-600">
                {t("move_supervisor_desc").replace("{name}", moving.name)}
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900 text-xs leading-relaxed">
                {t("move_supervisor_warning")}
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1">{t("move_target_area_manager")}</label>
                <select
                  value={moveTarget}
                  onChange={(e) => setMoveTarget(e.target.value)}
                  className="inp"
                  data-testid="move-admin-target-select"
                >
                  <option value="">— {t("select_area_manager")} —</option>
                  {areaManagers
                    .filter((am) => am.id !== moving.area_manager_id && am.is_active !== false)
                    .map((am) => (
                      <option key={am.id} value={am.id}>
                        {am.full_name || am.username} ({am.username})
                      </option>
                    ))}
                </select>
              </div>
              {moveError && (
                <div className="text-sm bg-rose-50 text-rose-700 rounded-lg p-2">{moveError}</div>
              )}
            </div>
            <div className="p-4 border-t border-gray-100 flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
              <button className="btn btn-secondary" onClick={closeMove}>{t("cancel")}</button>
              <button className="btn btn-primary bg-indigo-600 hover:bg-indigo-700" onClick={doMove} data-testid="move-admin-confirm-btn">
                <ArrowRightLeft size={14} /> {t("confirm_move")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EXPERIMENTAL: supervisor activity modal */}
      {activityFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="admin-activity-modal" onClick={() => setActivityFor(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-black text-slate-800 text-sm flex items-center gap-2">
                <Activity size={16} className="text-violet-600" /> {t("activity_title")} — {activityFor.admin_name}
              </h3>
              <div className="flex items-center gap-1">
                <button onClick={downloadActivity} className="inline-flex items-center gap-1 text-[11px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-lg px-2 py-1 hover:bg-teal-100" data-testid="activity-download-btn" title={t("activity_download")}>
                  ⬇ {t("activity_download")}
                </button>
                <button onClick={() => setActivityFor(null)} className="p-1 rounded-lg hover:bg-slate-100" data-testid="admin-activity-close"><X size={16} /></button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-violet-50 border border-violet-100 rounded-xl p-3">
                <div className="text-[10px] text-slate-500">{t("activity_last_seen")}</div>
                <div className="text-sm font-black text-violet-700" data-testid="activity-last-seen">{fmtSeen(activityFor.last_seen_at) || "—"}</div>
              </div>
              <div className={`rounded-xl p-3 border ${activityFor.week_pct >= 80 ? "bg-emerald-50 border-emerald-100" : "bg-slate-50 border-slate-100"}`}>
                <div className="text-[10px] text-slate-500">{t("activity_week_engagement")}</div>
                <div className={`text-sm font-black ${activityFor.week_pct >= 80 ? "text-emerald-700" : "text-slate-700"}`} data-testid="activity-week-pct">
                  {activityFor.week_pct}% · {t("activity_days_count", { n: activityFor.week_days })} {activityFor.week_pct >= 80 && "🌟"}
                </div>
              </div>
              <div className={`col-span-2 rounded-xl p-3 border ${(activityFor.month_pct || 0) >= 80 ? "bg-emerald-50 border-emerald-100" : "bg-sky-50 border-sky-100"}`}>
                <div className="text-[10px] text-slate-500">{t("activity_month_engagement")}</div>
                <div className={`text-sm font-black ${(activityFor.month_pct || 0) >= 80 ? "text-emerald-700" : "text-sky-700"}`} data-testid="activity-month-pct">
                  {activityFor.month_pct || 0}% · {t("activity_days_count", { n: activityFor.month_days || 0 })}
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs font-bold text-slate-600 mb-2 flex items-center gap-1.5"><Clock size={12} /> {t("activity_sessions")}</div>
              {(activityFor.sessions || []).length === 0 ? (
                <div className="text-xs text-slate-400">—</div>
              ) : (
                <div className="space-y-1.5">
                  {activityFor.sessions.map((s, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg px-3 py-1.5 text-xs" data-testid={`activity-session-${i}`}>
                      <span className="text-slate-700 font-semibold" dir="ltr">
                        {new Date(s.login_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} · {new Date(s.login_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <span className="font-black text-teal-700">{fmtDur(s.duration_min || 0)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
